import React, { useState, useEffect } from 'react';
import { Users, ClipboardCheck, Clock, AlertTriangle, ChevronRight, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, isOverdue } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function SupervisorDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray()
      ]);
      
      // Get team members (employees supervised by this user)
      const team = usersData.filter(u => u.supervisorId === user?.id && u.status === 'active');
      setTeamMembers(team);
      
      // Get team appraisals
      const teamAppraisals = appraisalsData.filter(a => team.some(m => m.id === a.userId) && a.visibleToSupervisor !== false);
      setAppraisals(teamAppraisals);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingReviews = appraisals.filter(a => a.status === 'employee_completed');
  const overdueAppraisals = appraisals.filter(a => isOverdue(a.dueDate) && a.status !== 'finalized');
  const completedAppraisals = appraisals.filter(a => a.status === 'finalized');

  const getUserById = (id) => teamMembers.find(u => u.id === id);

  // Chart data
  const performanceData = completedAppraisals.map(a => {
    const employee = getUserById(a.userId);
    return {
      name: `${employee?.profile?.firstName?.[0] || ''}${employee?.profile?.lastName?.[0] || ''}`,
      score: a.scores?.finalWeightedScore || 0
    };
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supervisor Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage your team's performance reviews</p>
        </div>
        <Button onClick={() => navigate('/supervisor/appraisals')} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white">
          <ClipboardCheck className="w-4 h-4 mr-2" /> View All Appraisals
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Team Size</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{teamMembers.length}</p>
                <p className="text-xs text-gray-400 mt-1">Direct reports</p>
              </div>
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Reviews</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{pendingReviews.length}</p>
                <p className="text-xs text-amber-600 mt-1">Need your attention</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Overdue</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{overdueAppraisals.length}</p>
                <p className="text-xs text-rose-600 mt-1">Past due date</p>
              </div>
              <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{completedAppraisals.length}</p>
                <p className="text-xs text-emerald-600 mt-1">This cycle</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <ClipboardCheck className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Reviews */}
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Pending Reviews</CardTitle>
              <Button variant="ghost" size="sm" className="text-violet-600" onClick={() => navigate('/supervisor/appraisals')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pendingReviews.length > 0 ? (
              <div className="space-y-4">
                {pendingReviews.slice(0, 5).map(appraisal => {
                  const employee = getUserById(appraisal.userId);
                  const daysUntil = getDaysUntil(appraisal.dueDate);
                  
                  return (
                    <div
                      key={appraisal.id}
                      className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:border-violet-200 hover:bg-violet-50/30 transition-all cursor-pointer"
                      onClick={() => navigate(`/supervisor/appraisals/${appraisal.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                          {employee?.profile?.firstName?.[0]}{employee?.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {employee?.profile?.firstName} {employee?.profile?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{appraisal.title}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400">Self Score: {appraisal.scores?.employeeSelfTotal?.toFixed(1) || 'N/A'}</span>
                            <span className={`text-xs ${daysUntil < 7 ? 'text-amber-600' : 'text-gray-400'}`}>
                              {daysUntil < 0 ? `Overdue by ${Math.abs(daysUntil)} days` : `Due in ${daysUntil} days`}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/supervisor/appraisals/${appraisal.id}`);
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">No pending reviews</p>
                <p className="text-sm text-gray-400 mt-1">All caught up!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Overview */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Team Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {teamMembers.length > 0 ? teamMembers.slice(0, 6).map(member => {
              const memberAppraisal = appraisals.find(a => a.userId === member.id);
              const progress = memberAppraisal?.progress?.overallPercent || 0;
              
              return (
                <div key={member.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {member.profile?.firstName?.[0]}{member.profile?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {member.profile?.firstName} {member.profile?.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{member.profile?.position}</p>
                      </div>
                    </div>
                    {memberAppraisal && (
                      <Badge className={getStatusBadgeColor(memberAppraisal.status)} variant="secondary">
                        {formatStatus(memberAppraisal.status)}
                      </Badge>
                    )}
                  </div>
                  {memberAppraisal && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                  )}
                </div>
              );
            }) : (
              <p className="text-sm text-gray-500 text-center py-4">No team members assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Chart */}
      {completedAppraisals.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-violet-500" />
              Team Performance Scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [value.toFixed(2), 'Score']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="score" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
